import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, Typography, FormHelperText, Select, MenuItem, TextField, InputAdornment, FormControl, Alert, Divider } from '@mui/material';
import { setSuccessfullModifyDialog } from '../../../redux/successfullModifyDialogSlice';
import { setContractDetailsData } from '../../../redux/contractDetailsDataSlice';
import { CustomSingleSelect } from '../../../utils/CustomSingleSelect';
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import { CustomSwitch } from '../../../utils/CustomSwitch';
import { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import Iconify from '../../Iconify';
import * as Yup from 'yup';
import axios from 'axios';
import '../../../css/contracts/addContractDialog.css';

export const EditContractMainData = ({ openEditContract, setOpenEditContract }) => {

    const { id } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [errMsg, setErrMsg] = useState(null);
    const [errDate, setErrDate] = useState(null);
    const contractDetailsData = useSelector((state) => state.ContractDetailsData);

    const formik = useFormik({

        initialValues: {
            title: contractDetailsData?.title,
            contract_number: contractDetailsData.contract_number,
            company: { value: contractDetailsData?.company.id, label: contractDetailsData?.company.name },
            start_date: contractDetailsData?.contract_date?.[contractDetailsData?.contract_date.length - 1]?.start_date,
            end_date: contractDetailsData?.contract_date?.[0]?.end_date ? contractDetailsData?.contract_date?.[0]?.end_date : '',
            visit_count: contractDetailsData?.visit_count,
            visit_frequency: contractDetailsData?.visit_frequency,
            auto_renew: contractDetailsData?.auto_renew,
            rappel_frequency: contractDetailsData.rappel_frequency || 0,
        },

        validationSchema: Yup.object().shape({
            title: Yup.string().required(t('isRequired')),
            company: Yup.object().required(t('isRequired')),
        }),

        onSubmit: async (values) => {

            const contractData = {
                title: values.title,
                contract_number: values.contract_number,
                company_id: values.company.value,
                end_date: values.end_date,
                visit_count: Number(values.visit_count),
                visit_frequency: values.visit_frequency,
                auto_renew: values.auto_renew,
                rappel_frequency: values.rappel_frequency,
            }
            try {
                const response = await axios.patch(`contract/updateMainData/${id}`, contractData);

                if (response.status === 200 || response.status === 201) {
                    dispatch(setSuccessfullModifyDialog({ open: true, type: "Modifier les informations du contrat" }));
                    dispatch(setContractDetailsData({ ...contractDetailsData, ...response.data }));
                    setOpenEditContract(false);
                }
            } catch (error) {
                console.error('Error editing contract main data:', error);
                CustomErrorLogger(error, 'Error editing contract main data');
                setErrMsg(error?.response?.data?.message || error.message || 'erreur lors de la modification du contrat');
            }
        },
    });

    const { handleSubmit, values, handleChange, handleBlur, touched, errors, setFieldValue } = formik;

    const handleClose = useCallback(() => {
        setOpenEditContract(false);
    }, [setOpenEditContract]);

    const handleCreateCompany = useCallback(async (newOption) => {
        try {
            const response = await axios.post('company', { name: newOption.label });
            if (response.status === 200 || response.status === 201) {
                setFieldValue('company', { value: response.data.id, label: response.data.name });
            }
        } catch (error) {
            console.error('Error creating company in editContractDialoge:', error);
            CustomErrorLogger(error, "Error creating company in editContractDialoge");
        }
    }, [setFieldValue]);

    const fetchCompanies = useCallback(async (offset, limit, search) => {
        try {
            const response = await axios.get('company', {
                params: {
                    offset,
                    limit,
                    filters: { debouncedSearch: search },
                },
            });
            if (response.status === 200 || response.status === 201) {
                return response.data.companiesData.map(company => ({
                    value: company.id,
                    label: company.name,
                }));
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
            CustomErrorLogger(error, "error fetching companies in editContractDialoge");
        }
    }, []);

    const handleFrequencyUnitChange = (event) => {
        setFieldValue('visit_frequency', event.target.value);
    };

    const handleFrequencyShortCut = (number, frequency) => {
        setFieldValue('visit_count', number);
        setFieldValue('visit_frequency', frequency);
    };

    const handleChangeContractStartDate = (event) => {
        const date = event.target.value;
        if (values.end_date && new Date(date) > new Date(values.end_date)) {
            setErrDate('La date de début de contrat doit être inférieure à la date de fin de contrat');
            return;
        }
        setErrDate(null);
        setFieldValue('start_date', date);
    }

    const handleChangeContractEndDate = (event) => {
        const date = event.target.value;
        if (new Date(date) < new Date(values.start_date)) {
            setErrDate('La date de fin de contrat doit être supérieure à la date de début de contrat');
            return;
        }
        setErrDate(null);
        setFieldValue('end_date', date);
    };

    useEffect(() => {
        if (!values.end_date) {
            setFieldValue('auto_renew', false);
            setFieldValue('rappel_frequency', 0);
        }
    }, [values.end_date, setFieldValue])

    return (
        <Dialog open={openEditContract} fullWidth maxWidth="md">
            <IconButton onClick={handleClose} className='addContract_icon_button'>
                <Iconify icon="material-symbols:close" className='addContract_close_icon' />
            </IconButton>
            <DialogTitle className='addContract_dialog_title'>Modification du contrat</DialogTitle>
            <Divider />
            {errMsg && <Alert severity="error">{errMsg}</Alert>}
            <form onSubmit={handleSubmit}>
                <DialogContent className='addContract_dialog_content'>
                    <Box className="addContract_title_number_box">
                        <Box className="addContract_helper_field_box">
                            <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Titre du contrat</FormHelperText>
                            <TextField
                                fullWidth
                                placeholder='Entrez le titre'
                                value={values.title}
                                className='addContract_InputField'
                                id="outlined-adornment-weight"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                name="title"
                                error={touched.title && Boolean(errors.title)}
                                helperText={touched.title && errors.title}
                                aria-describedby="outlined-weight-helper-text"
                            />
                        </Box>
                        <Box className="addContract_helper_field_box">
                            <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Numéro du contrat (optionnel)</FormHelperText>
                            <TextField
                                fullWidth
                                placeholder='Entrez le numero'
                                value={values.contract_number}
                                className='addContract_InputField'
                                id="outlined-adornment-weight"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                name="contract_number"
                                error={touched.contract_number && Boolean(errors.contract_number)}
                                helperText={touched.contract_number && errors.contract_number}
                                aria-describedby="outlined-weight-helper-text"
                            />
                        </Box>
                    </Box>
                    <Box className='addContract_helper_field_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Entreprise associée au contrat</FormHelperText>
                        <CustomSingleSelect
                            placeholder='Sélectionnez une entreprise'
                            className='addInvoice_InputField'
                            required={true}
                            value={values.company}
                            fetchFunction={fetchCompanies}
                            backendSearchable={true}
                            onChange={(value) => setFieldValue('company', value)}
                            creatable={true}
                            createFunction={handleCreateCompany}
                            helperText={touched.company && errors.company}
                            error={touched.company && Boolean(errors.company)}
                        />
                    </Box>

                    {errDate && <Alert severity="warning">{errDate}</Alert>}

                    <Box className="addContract_title_number_box">
                        <Box className='addContract_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Date de début</FormHelperText>
                            <TextField
                                disabled={true}
                                sx={{ width: '100%' }}
                                className={`addContract_InputField`}
                                id="outlined-adornment-weight"
                                type='date'
                                value={new Date(values.start_date).toISOString().split('T')[0]}
                                onChange={handleChangeContractStartDate}
                                onBlur={handleBlur}
                                name="start_date"
                                aria-describedby="outlined-weight-helper-text"
                                error={touched.start_date && Boolean(errors.start_date)}
                                helperText={touched.start_date && errors.start_date}
                            />
                        </Box>
                        <Box className='addContract_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Date de fin</FormHelperText>
                            <TextField
                                disabled={true}
                                sx={{ width: '100%' }}
                                className={`addContract_InputField`}
                                id="outlined-adornment-weight"
                                type='date'
                                value={values.end_date ? new Date(values.end_date).toISOString().split('T')[0] : ''}
                                onChange={handleChangeContractEndDate}
                                onBlur={handleBlur}
                                name="end_date"
                                aria-describedby="outlined-weight-helper-text"
                                error={touched.end_date && Boolean(errors.end_date)}
                                helperText={touched.end_date && errors.end_date}
                            />
                        </Box>
                    </Box>
                    <Box className="addContract_title_number_box">
                        <Box className='addContract_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Suivi des visites (optionnel)</FormHelperText>
                            <TextField
                                fullWidth
                                placeholder='Indiquez la fréquence des visites '
                                value={values.visit_count}
                                className='addContract_InputField'
                                id="outlined-adornment-weight"
                                onChange={(event) => setFieldValue('visit_count', event.target.value)}
                                onBlur={handleBlur}
                                name="contract_number"
                                error={touched.visit_count && Boolean(errors.visit_count)}
                                helperText={touched.visit_count && errors.visit_count}
                                aria-describedby="outlined-weight-helper-text"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Select
                                                value={values.visit_frequency}
                                                onChange={handleFrequencyUnitChange}
                                                displayEmpty
                                                sx={{ backgroundColor: '#F2F2F2' }}
                                            >
                                                <MenuItem value="year">ans</MenuItem>
                                                <MenuItem value="month">mois</MenuItem>
                                                <MenuItem value="week">semaine</MenuItem>
                                            </Select>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        paddingRight: '0px !important',
                                    },
                                }}
                            />
                            <Box className='addContract_frequency_shortcut_box'>
                                <Typography className='addContract_frequency_shortcut_item' onClick={() => handleFrequencyShortCut(3, 'month')}>3 mois</Typography>
                                <Typography className='addContract_frequency_shortcut_item' onClick={() => handleFrequencyShortCut(6, 'month')}>6 mois</Typography>
                                <Typography className='addContract_frequency_shortcut_item' onClick={() => handleFrequencyShortCut(1, 'year')}>1 an</Typography>
                            </Box>
                        </Box>
                        {values.end_date &&
                            <Box className='addContract_helper_field_box'>
                                <FormHelperText id="outlined-weight-helper-text" className="addContractInputTitle">Renouvellement automatique</FormHelperText>
                                <CustomSwitch checked={values.auto_renew} onChange={(e) => setFieldValue('auto_renew', e.target.checked)} />
                                {values.auto_renew &&
                                    <Box className='addContract_helper_field_box'>
                                        <Box id="outlined-weight-helper-text" className="addContract_rappel_box">
                                            <Iconify className='addContract_rappel_icon' icon="lucide:clock-fading" />
                                            <Typography className="addContractInputTitle" sx={{ margin: '0 !important' }}>Délai de préavis de non renouvellement</Typography>
                                        </Box>
                                        <FormControl fullWidth margin="dense" className='addContract_InputField'>
                                            <Select
                                                value={values.rappel_frequency}
                                                onChange={(e) => setFieldValue('rappel_frequency', e.target.value)}
                                            >
                                                <MenuItem value={3}>3 mois avant</MenuItem>
                                                <MenuItem value={6}>6 mois avant</MenuItem>
                                                <MenuItem value={8}>8 mois avant</MenuItem>
                                                <MenuItem value={0}>Aucun</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                }
                            </Box>
                        }
                    </Box>
                </DialogContent>
                <DialogActions className='addContract_dialog_actions'>
                    <Button className='addContract_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <Button className='addContract_button_valider' variant="contained" type="submit">{t('Valider')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};